import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { API_BASE_URL, toImageSrc } from '../../services/api';

export interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  image_url?: string;
  sort_order?: number;
}

interface CategoryShowcaseProps {
  categories: Category[];
}

const Section = styled.section`
  padding: clamp(80px, 12vw, 140px) 0;
  background: #ffffff;
`;

const Header = styled.div`
  max-width: 680px;
  margin: 0 auto clamp(40px, 8vw, 64px);
  text-align: center;
  display: grid;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 3.2vw, 2.8rem);
  font-weight: 600;
  color: #2c2a29;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(44, 42, 41, 0.7);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: clamp(20px, 4vw, 32px);
`;

const Card = styled(Link)`
  position: relative;
  display: block;
  border-radius: clamp(22px, 3vw, 28px);
  overflow: hidden;
  min-height: 260px;
  text-decoration: none;
  color: #fdfaf6;
  box-shadow: 0 22px 42px rgba(37, 45, 40, 0.16);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 30px 60px rgba(37, 45, 40, 0.22);
  }
`;

const CardBackground = styled.div<{ image?: string }>`
  position: absolute;
  inset: 0;
  background: ${({ image }) =>
    image
      ? `linear-gradient(180deg, rgba(19, 24, 21, 0.15), rgba(19, 24, 21, 0.7)), url('${image}') center/cover`
      : 'linear-gradient(155deg, rgba(47, 58, 52, 0.9), rgba(102, 87, 73, 0.8))'};
  transition: transform 0.4s ease;

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 1;
  display: grid;
  gap: 8px;
  padding: 28px;
  height: 100%;
  align-content: flex-end;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.78);
  margin: 0;
`;

const CardMeta = styled.span`
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
  margin-top: 6px;
  color: rgba(255, 255, 255, 0.7);
`;

const CategoryShowcase: React.FC<CategoryShowcaseProps> = ({ categories }) => {
  if (!categories.length) return null;

  const resolveCategoryImage = (src?: string) => {
    if (!src) return undefined;
    const stringSrc = String(src).trim();
    if (!stringSrc) return undefined;
    if (stringSrc.startsWith('http://') || stringSrc.startsWith('https://')) {
      return stringSrc;
    }
    if (stringSrc.startsWith('/static/')) {
      const baseHost = API_BASE_URL.replace(/\/api$/i, '');
      return `${baseHost}${stringSrc}`;
    }
    return toImageSrc(stringSrc);
  };

  return (
    <Section>
      <div className="container">
        <Header>
          <Title>Utforska våra signaturkategorier</Title>
          <Subtitle>
            Vi blandar floristik med inredningsdetaljer – handplockade kollektioner där varje kategori
            bjuder på noga valda favoriter för hemmet.
          </Subtitle>
        </Header>

        <Grid>
          {categories.map(category => {
            const image = resolveCategoryImage(category.image_url || `/static/kategori_${category.slug}.png`);

            return (
              <Card key={category.id} to={`/produkter?kategori=${category.slug}`}>
                <CardBackground image={image} />
                <CardContent>
                  <CardTitle>{category.name}</CardTitle>
                  {category.description && (
                    <CardDescription>{category.description}</CardDescription>
                  )}
                  <CardMeta>Visa kollektion</CardMeta>
                </CardContent>
              </Card>
            );
          })}
        </Grid>
      </div>
    </Section>
  );
};

export default CategoryShowcase;
